import { ArrowLeft, Inbox, PanelRight, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import type { DialogDto, DialogListFilters } from '../../api/types';
import { ContactPanel } from '../../components/dialogs/ContactPanel';
import { DialogConversation } from '../../components/dialogs/DialogConversation';
import { DialogFilters } from '../../components/dialogs/DialogFilters';
import { DialogListItem } from '../../components/dialogs/DialogListItem';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { useDialog, useDialogList, useSubscribeChannels } from '../../hooks/use-dialogs';
import { BP_LG, BP_XL, useMediaQuery } from '../../lib/use-media-query';
import { useLocalStorageState } from '../../lib/use-local-storage-state';

const DEFAULT_FILTERS: DialogListFilters = { limit: 50 };

/** Dialogs where the client is waiting on a human reply sort to the top of the list (§U1). */
function sortWaitingFirst<T extends { awaitingHuman: boolean; updatedAt: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    if (a.awaitingHuman !== b.awaitingHuman) return a.awaitingHuman ? -1 : 1;
    return b.updatedAt.localeCompare(a.updatedAt);
  });
}

/**
 * Two-pane inbox, collapsing to fit the viewport (bug fix — see docs/BACKLOG_V2.md):
 * - below 1024px (`lg`): one pane at a time — the dialog list, or the conversation with a
 *   "Назад к списку" back link once a dialog is selected. No contact card.
 * - 1024–1280px (`lg`–`xl`): list (narrower) + conversation; the contact card lives in a
 *   slide-out drawer opened from the header instead of a third fixed column, since 420px
 *   list + 288px card left almost nothing for the conversation at this width.
 * - 1280px+ (`xl`): list + conversation + contact card side by side, as before.
 */
export function InboxPage() {
  const { dialogId } = useParams<{ dialogId?: string }>();
  const [filters, setFiltersRaw] = useLocalStorageState<DialogListFilters>('od.inbox.filters', DEFAULT_FILTERS);
  const [search, setSearch] = useLocalStorageState('od.inbox.search', '');
  const [pages, setPages] = useState<DialogDto[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const query = useDialogList(filters);
  const selectedQuery = useDialog(dialogId);

  const isLg = useMediaQuery(BP_LG);
  const isXl = useMediaQuery(BP_XL);

  // "Ещё" accumulates pages client-side: a filter change resets the cursor and the accumulated list,
  // a load-more only appends the newly-fetched page.
  useEffect(() => {
    if (!query.data) return;
    setPages((prev) => (filters.cursor ? [...prev, ...query.data.items] : query.data.items));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data]);

  // Closing the contact drawer when the dialog changes or the layout no longer needs it
  // avoids a stray open drawer following you to the next conversation.
  useEffect(() => {
    setDrawerOpen(false);
  }, [dialogId, isLg, isXl]);

  function setFilters(next: DialogListFilters) {
    setFiltersRaw(next);
  }

  function loadMore() {
    if (query.data?.nextCursor) setFiltersRaw({ ...filters, cursor: query.data.nextCursor });
  }

  const items = useMemo(() => pages, [pages]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q
      ? items.filter((d) => {
          const name = `${d.contact.firstName ?? ''} ${d.contact.lastName ?? ''}`.toLowerCase();
          return name.includes(q) || String(d.contact.vkUserId).includes(q);
        })
      : items;
    return sortWaitingFirst(base);
  }, [items, search]);

  const channelIds = useMemo(() => [...new Set(items.map((d) => d.channelId))], [items]);
  useSubscribeChannels(channelIds);

  // Below `lg` it's one pane at a time; at `lg`+ both list and conversation show together.
  const showList = isLg || !dialogId;
  const showConversation = isLg || Boolean(dialogId);
  // The inline third column only fits from `xl`; between `lg` and `xl` it's a drawer instead.
  const showInlineContactPanel = isXl;
  const showDrawerToggle = isLg && !isXl;

  return (
    <div className="relative flex h-full">
      {showList && (
        <div className="flex w-full shrink-0 flex-col border-r border-border lg:w-[300px] xl:w-[420px]">
          <DialogFilters value={filters} onChange={setFilters} search={search} onSearchChange={setSearch} items={items} />
          <div className="flex-1 overflow-y-auto">
            {query.isLoading && (
              <div className="flex flex-col gap-2 p-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            )}
            {query.isError && (
              <p className="p-4 text-sm text-red-600">Не удалось загрузить диалоги. Попробуйте обновить страницу.</p>
            )}
            {!query.isLoading && !query.isError && filtered.length === 0 && (
              <p className="p-6 text-center text-sm text-muted-fg">Диалогов не найдено.</p>
            )}
            {filtered.map((dialog) => (
              <DialogListItem
                key={dialog.id}
                dialog={dialog}
                to={`/inbox/${dialog.id}`}
                active={dialog.id === dialogId}
                searchQuery={search}
              />
            ))}
            {!query.isLoading && query.data?.nextCursor && (
              <div className="p-3">
                <Button variant="secondary" size="sm" className="w-full" onClick={loadMore}>
                  Ещё
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
      {showConversation && (
        <div className="flex min-w-0 flex-1">
          {!dialogId && (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 text-muted-fg">
              <Inbox className="h-8 w-8" />
              <p className="text-sm">Выберите диалог слева</p>
            </div>
          )}
          {dialogId && selectedQuery.isLoading && (
            <div className="flex flex-1 flex-col gap-2 p-4">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-full w-full" />
            </div>
          )}
          {dialogId && (selectedQuery.isError || (!selectedQuery.isLoading && !selectedQuery.data)) && (
            <div className="flex flex-1 flex-col items-center justify-center gap-2">
              <p className="text-sm text-red-600">Диалог не найден.</p>
            </div>
          )}
          {selectedQuery.data && (
            <>
              <DialogConversation
                dialog={selectedQuery.data}
                showOpenFullLink
                headerLeft={
                  !isLg ? (
                    <Link
                      to="/inbox"
                      className="flex shrink-0 items-center gap-1 rounded p-1 text-sm text-muted-fg hover:bg-muted hover:text-fg"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      Назад к списку
                    </Link>
                  ) : undefined
                }
                headerRight={
                  showDrawerToggle ? (
                    <button
                      type="button"
                      aria-label="Карточка контакта"
                      title="Карточка контакта"
                      onClick={() => setDrawerOpen(true)}
                      className="rounded p-1 text-muted-fg hover:bg-muted hover:text-fg"
                    >
                      <PanelRight className="h-4 w-4" />
                    </button>
                  ) : undefined
                }
              />
              {showInlineContactPanel && <ContactPanel dialog={selectedQuery.data} />}
            </>
          )}
        </div>
      )}
      {showDrawerToggle && drawerOpen && selectedQuery.data && (
        <div className="absolute inset-0 z-40 flex justify-end">
          <button
            type="button"
            aria-label="Закрыть карточку контакта"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-black/30"
          />
          <div className="relative flex h-full shadow-xl">
            <ContactPanel dialog={selectedQuery.data} />
            <button
              type="button"
              aria-label="Закрыть"
              onClick={() => setDrawerOpen(false)}
              className="absolute right-2 top-2 rounded p-1 text-muted-fg hover:bg-muted hover:text-fg"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
